import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/hooks/use-toast";

export default function AcceptInvitation() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [invitation, setInvitation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!token) {
      console.log("🔍 [DEBUG] Token não encontrado na URL");
      setError("Token de convite inválido");
      setLoading(false);
      return;
    }

    console.log("🔍 [DEBUG] Token recebido:", token);
    fetchInvitation();
  }, [token]);

  const fetchInvitation = async () => {
    try {
      console.log("🔍 [DEBUG] Buscando convite com token:", token);
      
      // First try to get active invitation
      const { data, error } = await supabase
        .rpc('invitation_lookup', { p_invitation_id: token });

      console.log("🔍 [DEBUG] Resultado invitation_lookup:", { data, error });

      if (error) {
        console.error('Invitation lookup error:', error);
        setError("Erro ao buscar convite");
        setLoading(false);
        return;
      }

      if (!data || data.length === 0) {
        // If not found in active invitations, check if it exists in any state
        console.log("🔍 [DEBUG] Convite não encontrado em estado ativo, verificando status...");
        
        const { data: invitationData, error: invError } = await supabase
          .from('company_invitations')
          .select(`
            *,
            companies:company_id (
              name,
              slug
            )
          `)
          .eq('id', token)
          .single();
          
        if (invError || !invitationData) {
          console.log("🔍 [DEBUG] Convite não encontrado na base de dados");
          setError("Convite não encontrado. Verifique se o link está correto.");
          setLoading(false);
          return;
        }
        
        // Check invitation status and provide specific messages
        if (invitationData.status === 'accepted') {
          console.log("🔍 [DEBUG] Convite já foi aceito");
          
          // Check if the current user is already a member
          if (user) {
            const { data: membership } = await supabase
              .from('user_companies')
              .select('role, company_id')
              .eq('user_id', user.id)
              .eq('company_id', invitationData.company_id)
              .single();
              
            if (membership) {
              const companySlug = invitationData.companies?.slug;
              const dashboardPath = membership.role === 'company_admin' 
                ? `/company/${companySlug}/admin`
                : `/company/${companySlug}/user`;
              
              setError(`Este convite já foi aceito e você já é membro da empresa. Redirecionando para o dashboard...`);
              setTimeout(() => navigate(dashboardPath), 2000);
              setLoading(false);
              return;
            }
          }
          
          setError("Este convite já foi aceito. Se você deveria ter acesso à empresa, entre em contato com o administrador.");
          setLoading(false);
          return;
        } else if (invitationData.expires_at && new Date(invitationData.expires_at) < new Date()) {
          console.log("🔍 [DEBUG] Convite expirado");
          setError("Este convite expirou. Solicite um novo convite ao administrador da empresa.");
          setLoading(false);
          return;
        } else {
          console.log("🔍 [DEBUG] Convite em status inválido:", invitationData.status);
          setError("Convite em estado inválido. Entre em contato com o administrador.");
          setLoading(false);
          return;
        }
      }

      const inviteData = data[0];
      console.log("🔍 [DEBUG] Dados do convite encontrado:", inviteData);
      
      // Transform the data to match the expected structure
      const transformedData = {
        id: inviteData.id,
        email: inviteData.email,
        full_name: inviteData.full_name,
        role: inviteData.role,
        expires_at: inviteData.expires_at,
        status: inviteData.status,
        company_id: inviteData.company_id,
        company: {
          name: inviteData.company_name,
          slug: inviteData.company_slug
        }
      };

      setInvitation(transformedData);
      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching invitation:', err);
      setError("Erro ao carregar convite");
      setLoading(false);
    }
  };

  const acceptInvitation = async () => {
    if (!invitation || !user) {
      console.log("🔍 [DEBUG] Dados faltando para aceitar convite:", { invitation: !!invitation, user: !!user });
      return;
    }

    console.log("🔍 [DEBUG] Iniciando aceitação do convite:", {
      invitationId: invitation.id,
      userEmail: user.email,
      invitationEmail: invitation.email
    });

    setProcessing(true);
    try {
      const { data, error } = await supabase
        .rpc('accept_invitation', { p_invitation_id: invitation.id });

      console.log("🔍 [DEBUG] Resultado accept_invitation:", { data, error });

      if (error) {
        console.error('Error accepting invitation:', error);
        console.error('Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        
        // Handle specific error cases
        let errorMessage = "Erro ao aceitar o convite";
        if (error.message === 'not_authenticated') {
          errorMessage = "Você precisa estar logado para aceitar o convite";
        } else if (error.message === 'invitation_invalid_or_expired') {
          errorMessage = "Convite inválido ou expirado";
        } else if (error.message === 'email_mismatch') {
          errorMessage = `Este convite é para ${invitation.email}, mas você está logado como ${user.email}. Faça logout e entre com a conta correta.`;
        } else if (error.message === 'user_email_not_found') {
          errorMessage = "Email do usuário não encontrado. Tente fazer logout e login novamente.";
        } else {
          errorMessage = `Erro ao aceitar o convite: ${error.message}`;
        }
        
        toast({
          title: "Erro",
          description: errorMessage,
          variant: "destructive",
        });
        return;
      }

      if (!data || data.length === 0) {
        toast({
          title: "Erro",
          description: "Erro ao processar convite",
          variant: "destructive",
        });
        return;
      }

      const result = data[0];
      
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['company-users', result.company_id] });
      queryClient.invalidateQueries({ queryKey: ['company-invitations', result.company_id] });
      
      toast({
        title: "Convite aceito!",
        description: `Bem-vindo à ${invitation.company.name}`,
      });

      // Redirect to appropriate dashboard based on role
      const dashboardPath = result.assigned_role === 'company_admin' 
        ? `/company/${result.company_slug}/admin`
        : `/company/${result.company_slug}/user`;
      navigate(dashboardPath);
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      toast({
        title: "Erro",
        description: "Erro ao aceitar convite",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <CardTitle>Convite Inválido</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-3">
            <Button onClick={() => navigate("/")} variant="outline">
              Voltar ao Início
            </Button>
            {error?.includes("está logado como") && (
              <Button 
                onClick={() => navigate("/login")} 
                variant="default"
                className="w-full"
              >
                Fazer Login com Outra Conta
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Convite para {invitation?.company?.name}</CardTitle>
            <CardDescription>
              Você foi convidado como {invitation?.role === 'company_admin' ? 'Administrador' : 'Usuário de Módulo'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertDescription>
                Para aceitar este convite, você precisa fazer login ou criar uma conta.
              </AlertDescription>
            </Alert>
            <div className="flex flex-col gap-2">
              <Button onClick={() => navigate(`/login?redirect=/accept-invitation/${token}`)}>
                Fazer Login
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate(`/signup?redirect=/accept-invitation/${token}`)}
              >
                Criar Conta
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
          <CardTitle>Convite para {invitation?.company?.name}</CardTitle>
          <CardDescription>
            Você foi convidado por {invitation?.invited_by} como {invitation?.role === 'company_admin' ? 'Administrador' : 'Usuário de Módulo'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertDescription>
              Ao aceitar este convite, você terá acesso aos sistemas e módulos da empresa.
            </AlertDescription>
          </Alert>
          <div className="flex flex-col gap-2">
            <Button 
              onClick={acceptInvitation} 
              disabled={processing}
              className="w-full"
            >
              {processing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                "Aceitar Convite"
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate("/")}
              className="w-full"
            >
              Cancelar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}